// Single shared Shiki highlighter. We lazy-load it and memoize so the worker / WASM cost is
// paid once, not per result row.
//
// The set of languages here matches what's actually indexed (see indexer config DEFAULT_INCLUDE_PATTERNS).
// Shiki silently falls back to plaintext for unknown langs, which is fine for us.

import {
  createHighlighter,
  type Highlighter,
  type BundledLanguage,
  type BundledTheme,
} from 'shiki';

const LANGS: BundledLanguage[] = [
  'python', 'typescript', 'tsx', 'javascript', 'jsx',
  'rust', 'go', 'java', 'c', 'cpp', 'csharp',
  'sql', 'bash', 'graphql', 'markdown', 'json', 'yaml', 'toml', 'html', 'css',
];

const THEMES: BundledTheme[] = ['github-light', 'github-dark'];

let _highlighter: Promise<Highlighter> | null = null;

export function getHighlighter(): Promise<Highlighter> {
  if (_highlighter === null) {
    _highlighter = createHighlighter({ themes: THEMES, langs: LANGS });
  }
  return _highlighter;
}

// Best-effort: map our backend's language tag to a Shiki BundledLanguage. Anything we don't
// recognize falls back to plaintext (Shiki accepts unknown -> ts default; we prefer explicit
// plaintext so a misclassified .md file doesn't get JS coloring).
export function shikiLang(lang: string | null | undefined): string {
  if (!lang) return 'text';
  const l = lang.toLowerCase();
  if ((LANGS as string[]).includes(l)) return l;
  // Common aliases the backend might emit (cocoindex's language tags don't perfectly match Shiki).
  const aliases: Record<string, string> = {
    js: 'javascript', ts: 'typescript', py: 'python',
    sh: 'bash', zsh: 'bash', md: 'markdown', gql: 'graphql',
  };
  return aliases[l] ?? 'text';
}

export type Theme = 'light' | 'dark';

export function shikiTheme(theme: Theme): BundledTheme {
  return theme === 'dark' ? 'github-dark' : 'github-light';
}
